import clsx from "clsx"
import { UISheet, UISheetType } from "../types/UITypes"
import MatrixPreviewRenderer from "./sheet-renderer/MatrixPreviewRenderer"
import SheetPreviewRenderer from "./sheet-renderer/SheetPreviewRenderer"
import { useMemo, useState } from "react"

type PublishApiModalProps = {
  open: boolean
  onClose: () => void
  onPublishToFirebase: (selectedApis: Record<string, boolean>) => void
  onPublish: (
    deploymentEnv: string,
    selectedApis: Record<string, boolean>
  ) => Promise<void>
  userType: string
  apiPreview: any
  canvasId: string
  enabledFeatures?: string[]
  validateApi: (sheetId: string) => void
  testApi: (sheetId: string) => void
}

export const PublishApiModal = (props: PublishApiModalProps) => {
  const {
    open,
    onClose,
    userType,
    onPublishToFirebase,
    onPublish,
    apiPreview,
    canvasId,
    enabledFeatures,
    validateApi,
    testApi,
  } = props

  const [isDeployButtonDisabled, setIsDeployButtonDisabled] = useState(false)

  const [
    isPublishSuccessIndicatorVisible,
    setIsPublishSuccessIndicatorVisible,
  ] = useState(false)

  const [deploymentEnv, setDeploymentEnv] = useState("staging")

  const handlePublish = async () => {
    setIsDeployButtonDisabled(true)

    await onPublish(deploymentEnv, publishedApis)

    setIsDeployButtonDisabled(false)

    setIsPublishSuccessIndicatorVisible(true)

    setTimeout(() => setIsPublishSuccessIndicatorVisible(false), 5000)
  }

  const [publishedApis, setPublishedApis] = useState<Record<string, boolean>>(
    {}
  )

  const [showDetails, setShowDetails] = useState<Record<string, boolean>>({})

  const isFirebaseExportEnabled = useMemo(
    () => enabledFeatures?.includes("firebase_export"),
    [enabledFeatures]
  )

  const isCloudflareExportEnabled = useMemo(
    () => enabledFeatures?.includes("cloudflare_export"),
    [enabledFeatures]
  )

  if (!open) {
    return null
  }

  return (
    <>
      <div onClick={onClose} className="overlay"></div>
      <div className="modal">
        <div className="deploy-controls">
          <div
            className={clsx("deploy-success-indicator", {
              "deploy-success-indicator-visible":
                isPublishSuccessIndicatorVisible,
            })}
          >
            Published
          </div>
          <div className="deploy-controls-spacer" />
          {isFirebaseExportEnabled && (
            <button
              onClick={() => onPublishToFirebase(publishedApis)}
              className={clsx("blue-button", {
                "disabled-button": isDeployButtonDisabled,
              })}
              disabled={isDeployButtonDisabled}
              style={{ marginRight: 10 }}
            >
              Export to Firebase
            </button>
          )}
          {isCloudflareExportEnabled && (
            <>
              <button
                onClick={handlePublish}
                className={clsx("blue-button", {
                  "disabled-button": isDeployButtonDisabled,
                })}
                disabled={isDeployButtonDisabled}
              >
                Publish
              </button>

              <div className="deploy-controls-spacer" />
              <select
                onChange={({ target }) => setDeploymentEnv(target.value)}
                value={deploymentEnv}
              >
                <option value="dev">Dev</option>
                <option value="staging">Staging</option>
                <option value="prod">Production</option>
              </select>
            </>
          )}
          <div className="deploy-controls-spacer" />
          <button onClick={onClose} className="view-blue-button blue-button">
            Hide Preview
          </button>
        </div>
        {apiPreview?.map((apiPreviewItem: any, i: number) => (
          <div
            key={`apiPreview-${apiPreviewItem.sheetId}`}
            style={{
              padding: 25,
              paddingTop: i === 0 ? 0 : 40,
              borderBottom:
                i + 1 < apiPreview.length ? "1px solid #ccc" : "none",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 20,
              }}
            >
              <input
                type="checkbox"
                style={{ marginRight: 6 }}
                checked={publishedApis[apiPreviewItem.sheetId]}
                onChange={({ target }) =>
                  setPublishedApis({
                    ...publishedApis,
                    [apiPreviewItem.sheetId]: target.checked,
                  })
                }
              />
              <b>API Name:</b>&nbsp;{apiPreviewItem.name}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  color: "#474747",
                  marginBottom: 15,
                }}
                onClick={() => {
                  setShowDetails({
                    ...showDetails,
                    [apiPreviewItem.sheetId]:
                      !showDetails[apiPreviewItem.sheetId],
                  })
                }}
              >
                {showDetails[apiPreviewItem.sheetId]
                  ? "Hide Details"
                  : "Show Details"}
              </div>
              <div
                style={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  color: "#474747",
                  marginBottom: 15,
                  marginLeft: 10,
                }}
                onClick={() => {
                  validateApi(apiPreviewItem.sheetId)
                  onClose()
                }}
              >
                Validate
              </div>
              <div
                style={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  color: "#474747",
                  marginBottom: 15,
                  marginLeft: 10,
                }}
                onClick={() => {
                  testApi(apiPreviewItem.sheetId)
                  onClose()
                }}
              >
                Test
              </div>
            </div>
            {showDetails[apiPreviewItem.sheetId] ? (
              <>
                {isCloudflareExportEnabled && (
                  <div style={{ marginBottom: 20 }}>
                    <b>API URL:</b>
                    <div
                      style={{
                        fontFamily: "monospace",
                        fontSize: 14,
                        marginTop: 5,
                      }}
                    >{`https://canvas-logic-${canvasId}-${deploymentEnv}.fyngo.workers.dev/`}</div>
                  </div>
                )}
                <div>
                  <b>Request Payload:</b>
                </div>
                <pre>
                  {JSON.stringify(apiPreviewItem?.requestPayload, null, 2)}
                </pre>
                <div style={{ marginTop: 20, marginBottom: 8 }}>
                  <b>Response Preview:</b>
                </div>
                {(apiPreviewItem.sheetData?.data as UISheet).entityType ===
                UISheetType.MATRIX ? (
                  <MatrixPreviewRenderer
                    id={`${apiPreviewItem.sheetId}`}
                    data={apiPreviewItem.sheetData?.data as UISheet}
                    key={`${apiPreviewItem.sheetId}`}
                  />
                ) : (
                  <SheetPreviewRenderer
                    id={`${apiPreviewItem.sheetId}`}
                    data={apiPreviewItem.sheetData?.data as UISheet}
                    key={`${apiPreviewItem.sheetId}`}
                  />
                )}

                <button
                  style={{ marginTop: 20 }}
                  onClick={() => {
                    navigator.clipboard
                      .writeText(
                        `curl -d '${JSON.stringify(
                          apiPreviewItem.requestPayload
                        )}' -u admin:admin -H "Content-Type: application/json" -X POST https://canvas-logic-${canvasId}-${deploymentEnv}.fyngo.workers.dev/`
                      )
                      .then(
                        function () {
                          alert("cURL is copied to clipboard")
                        },
                        function () {
                          alert("Could not copy cURL to clipboard")
                        }
                      )
                  }}
                >
                  Copy cURL
                </button>
              </>
            ) : null}
          </div>
        ))}
        {!apiPreview?.length && (
          <div>
            <div
              style={{
                fontSize: 18,
                marginTop: 25,
                marginBottom: 40,
                color: "#474747",
                textAlign: "center",
              }}
            >
              Add a sheet to the canvas to generate an API
            </div>
          </div>
        )}
      </div>
    </>
  )
}
